import { onScopeDispose, ref, watch, type Ref } from 'vue'
import type { SubtitleTrack } from '../types'
import type { UseHLSReturn } from './useHLS'

/** The voice a cue is attributed to, as the caption's `.json` rendering carries it. */
export interface TranscriptSpeaker {
  /** The diarisation label — stable, cited by every cue of the voice. */
  label: string
  /** What somebody named the voice, or `null`. */
  name: string | null
  /** `#RRGGBB`, or `null`. */
  color: string | null
}

export interface TranscriptCue {
  id: string
  start: number
  end: number
  text: string
  speaker?: TranscriptSpeaker | null
}

interface UseTranscriptOptions {
  currentTime: Ref<number>
  availableSubtitleTracks: Ref<SubtitleTrack[]>
  getSubtitleTrackUrl: UseHLSReturn['getSubtitleTrackUrl']
  seek: (time: number) => void
}

export interface UseTranscriptReturn {
  cues: Ref<TranscriptCue[]>
  activeCueIndex: Ref<number>
  selectedTrackIndex: Ref<number>
  isLoading: Ref<boolean>
  error: Ref<string | null>
  /**
   * The track's caption document, signed — `/subtitles/{uid}.vtt?sig=…` —, or `null` when the
   * track did not come from one. The same signature serves its other renderings (`.json`,
   * `.transcript.txt`), which is what the download reads.
   */
  documentUrl: Ref<string | null>
  selectTrack: (trackIndex: number) => Promise<void>
  seekToCue: (cueIndex: number) => void
}

/**
 * Find the active cue index using binary search.
 * Cues are sorted by start time — finds the last cue where start <= time < end.
 */
function findActiveCueIndex(cues: TranscriptCue[], time: number): number {
  if (cues.length === 0) return -1

  let low = 0
  let high = cues.length - 1
  let result = -1

  while (low <= high) {
    const mid = (low + high) >>> 1
    if (cues[mid].start <= time) {
      result = mid
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  if (result !== -1 && time < cues[result].end) {
    return result
  }

  return -1
}

function parseVttTimestamp(ts: string): number {
  const parts = ts.split(':')
  if (parts.length === 3) {
    return parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseFloat(parts[2])
  }
  if (parts.length === 2) {
    return parseInt(parts[0], 10) * 60 + parseFloat(parts[1])
  }
  return 0
}

/** The character references a cue spells by name: WebVTT's own escapes, and HTML's two quotes. */
const NAMED_REFERENCES = new Map([
  ['amp', '&'],
  ['lt', '<'],
  ['gt', '>'],
  ['nbsp', ' '],
  ['lrm', '‎'],
  ['rlm', '‏'],
  ['quot', '"'],
  ['apos', "'"],
])

/**
 * `&amp;`, `&nbsp;`, `&#233;`, `&#xE9;` as the characters they stand for. A name outside
 * {@link NAMED_REFERENCES}, or a number that is no character, stays as it is written.
 */
function decodeReferences(text: string): string {
  return text.replace(/&(#[0-9]+|#[xX][0-9a-fA-F]+|[A-Za-z]+);/g, (written, reference: string) => {
    if (!reference.startsWith('#')) return NAMED_REFERENCES.get(reference) ?? written
    const hex = reference[1] === 'x' || reference[1] === 'X'
    const code = parseInt(reference.slice(hex ? 2 : 1), hex ? 16 : 10)
    const isCharacter = code > 0 && code <= 0x10ffff && (code < 0xd800 || code > 0xdfff)
    return isCharacter ? String.fromCodePoint(code) : written
  })
}

/**
 * A cue's text as the panel reads it: its lines joined, its WebVTT markup — `<i>`, `<c.yellow>`,
 * `<v Roger>` — removed, and its character references decoded once the markup is gone, so that a
 * `&lt;` is read as a `<` and never as the start of a tag. Both renderings carry the text as it was
 * stored, markup and references included; a cue's voice is the `.json` rendering's `speaker`,
 * never a `<v>` tag.
 */
function plainText(text: string): string {
  return decodeReferences(text.replace(/<[^>]+>/g, ''))
    .replace(/\s*\n\s*/g, ' ')
    .trim()
}

function parseVttContent(vtt: string, baseOffset: number): TranscriptCue[] {
  const cues: TranscriptCue[] = []
  const lines = vtt.trim().split('\n')
  let i = 0

  // Skip header
  while (i < lines.length && !lines[i].includes('-->')) {
    // Detect X-TIMESTAMP-MAP to adjust segment timestamps
    const mapMatch = lines[i].match(/X-TIMESTAMP-MAP.*?LOCAL:(\d{2}:\d{2}:\d{2}\.\d{3}).*?MPEGTS:(\d+)/i)
    if (mapMatch) {
      const localTime = parseVttTimestamp(mapMatch[1])
      const mpegts = parseInt(mapMatch[2], 10)
      // MPEGTS is in 90kHz clock ticks
      baseOffset = (mpegts / 90000) - localTime
    }
    i++
  }

  while (i < lines.length) {
    const line = lines[i].trim()

    const tsMatch = line.match(/(\d{2}:\d{2}:\d{2}\.\d{3}|\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}\.\d{3}|\d{2}:\d{2}\.\d{3})/)
    if (tsMatch) {
      const start = parseVttTimestamp(tsMatch[1]) + baseOffset
      const end = parseVttTimestamp(tsMatch[2]) + baseOffset
      i++

      const textLines: string[] = []
      while (i < lines.length && lines[i].trim() !== '') {
        textLines.push(lines[i].trim())
        i++
      }

      const text = plainText(textLines.join('\n'))

      if (text) {
        cues.push({ id: `cue-${cues.length}`, start, end, text })
      }
    } else {
      i++
    }
  }

  return cues
}

/** Whether an address is this product's caption document in its WebVTT rendering. */
function isCaptionDocument(url: string): boolean {
  try {
    return /\/subtitles\/[^/]+\.vtt$/.test(new URL(url, window.location.href).pathname)
  } catch {
    return false
  }
}

/** The same signed document in another rendering: `.vtt` → `.json`, `.transcript.txt`, … */
export function renditionOf(documentUrl: string, extension: string): string {
  return documentUrl.replace(/\.vtt(\?|$)/, `.${extension}$1`)
}

interface JsonCue {
  id?: unknown
  start?: unknown
  end?: unknown
  text?: unknown
  speaker?: { label?: unknown; display_name?: unknown; color?: unknown } | null
}

/** The `.json` rendering's cues — seconds, and a speaker where the track has voices. */
function cuesFromJson(data: unknown): TranscriptCue[] | null {
  if (!Array.isArray(data)) return null
  const cues: TranscriptCue[] = []
  for (const raw of data as JsonCue[]) {
    if (typeof raw.start !== 'number' || typeof raw.end !== 'number' || typeof raw.text !== 'string') continue
    const text = plainText(raw.text)
    if (!text) continue
    const speaker = raw.speaker && typeof raw.speaker.label === 'string'
      ? {
          label: raw.speaker.label,
          name: typeof raw.speaker.display_name === 'string' ? raw.speaker.display_name : null,
          color: typeof raw.speaker.color === 'string' ? raw.speaker.color : null,
        }
      : null
    cues.push({ id: `cue-${cues.length}`, start: raw.start, end: raw.end, text, speaker })
  }
  return cues
}

/**
 * The cues of a signed caption document's `.json` rendering, or `null` when it cannot be read or
 * reads as no cue at all — the VTT is read instead either way.
 */
async function fetchJsonRendering(documentUrl: string, signal: AbortSignal): Promise<TranscriptCue[] | null> {
  try {
    const response = await fetch(renditionOf(documentUrl, 'json'), { signal })
    if (!response.ok) return null
    const cues = cuesFromJson(await response.json())
    return cues && cues.length > 0 ? cues : null
  } catch (e: any) {
    if (e?.name === 'AbortError') throw e
    return null
  }
}

function resolveUrl(base: string, relative: string): string {
  if (relative.startsWith('http://') || relative.startsWith('https://')) return relative
  const baseUrl = new URL(base)
  return new URL(relative, baseUrl).href
}

/** What one load of a track read: its cues, and the caption document they came from, if any. */
interface LoadedTrack {
  cues: TranscriptCue[]
  documentUrl: string | null
}

async function fetchSegmentedSubtitles(m3u8Url: string, signal: AbortSignal): Promise<LoadedTrack> {
  const response = await fetch(m3u8Url, { signal })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)

  const playlist = await response.text()
  const lines = playlist.split('\n')
  const segmentUrls: string[] = []

  for (const line of lines) {
    const trimmed = line.trim()
    if (trimmed && !trimmed.startsWith('#')) {
      segmentUrls.push(resolveUrl(m3u8Url, trimmed))
    }
  }

  if (segmentUrls.length === 0) throw new Error('No segments found')

  // A track this product serves is one segment, answered by a redirect to the signed caption
  // document: its `.json` rendering carries the voices the VTT has no room for. The redirect names
  // the document as soon as the headers arrive, so the JSON is asked for before the VTT's body is
  // read, and that body is read only when the JSON gives no cue — dropped otherwise.
  if (segmentUrls.length === 1) {
    const response = await fetch(segmentUrls[0]!, { signal })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    if (!isCaptionDocument(response.url)) return { cues: parseVttContent(await response.text(), 0), documentUrl: null }
    const fromJson = await fetchJsonRendering(response.url, signal)
    if (fromJson) {
      response.body?.cancel().catch(() => {})
      return { cues: fromJson, documentUrl: response.url }
    }
    return { cues: parseVttContent(await response.text(), 0), documentUrl: response.url }
  }

  const segments = await Promise.all(
    segmentUrls.map(url => fetch(url, { signal }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.text()
    })),
  )

  const allCues: TranscriptCue[] = []
  for (const vtt of segments) {
    allCues.push(...parseVttContent(vtt, 0))
  }

  return { cues: allCues, documentUrl: null }
}

/**
 * The cues sorted by start, whatever rendering or segments they came from — `findActiveCueIndex`
 * searches them by halves — with their ids reassigned in that order.
 */
function inReadingOrder(cues: TranscriptCue[]): TranscriptCue[] {
  cues.sort((a, b) => a.start - b.start)
  cues.forEach((cue, index) => {
    cue.id = `cue-${index}`
  })
  return cues
}

/** One track's cues, from its HLS playlist or from a caption file it addresses directly. */
async function fetchTrack(trackUrl: string, signal: AbortSignal): Promise<LoadedTrack> {
  if (trackUrl.includes('.m3u8')) return fetchSegmentedSubtitles(trackUrl, signal)

  // Only this product's caption document has a JSON rendering: tried first, then the raw VTT.
  const documentUrl = isCaptionDocument(trackUrl) ? trackUrl : null
  const fromJson = documentUrl ? await fetchJsonRendering(documentUrl, signal) : null
  if (fromJson) return { cues: fromJson, documentUrl }
  const vttResponse = await fetch(trackUrl, { signal })
  if (!vttResponse.ok) throw new Error(`HTTP ${vttResponse.status}`)
  return { cues: parseVttContent(await vttResponse.text(), 0), documentUrl }
}

/** The track a new set of tracks opens on: its default, else its first — `-1` when there is none. */
function defaultTrackIndex(tracks: SubtitleTrack[]): number {
  if (tracks.length === 0) return -1
  return (tracks.find((track) => track.isDefault) ?? tracks[0]!).index
}

export function useTranscript(options: UseTranscriptOptions): UseTranscriptReturn {
  const { currentTime, availableSubtitleTracks, getSubtitleTrackUrl, seek } = options

  const cues = ref<TranscriptCue[]>([])
  const activeCueIndex = ref(-1)
  const selectedTrackIndex = ref(-1)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const documentUrl = ref<string | null>(null)

  // The load in flight, if any. A newer selection aborts it, and a load writes what it read — or
  // ends the loading state — only while it is still this one: an answer landing after its load was
  // superseded touches nothing.
  let currentLoad: AbortController | null = null

  async function selectTrack(trackIndex: number): Promise<void> {
    currentLoad?.abort()
    currentLoad = null

    selectedTrackIndex.value = trackIndex
    cues.value = []
    activeCueIndex.value = -1
    error.value = null
    documentUrl.value = null
    isLoading.value = false

    if (trackIndex === -1) return

    const trackUrl = getSubtitleTrackUrl(trackIndex)
    if (!trackUrl) {
      error.value = 'formatNotSupported'
      return
    }

    const load = new AbortController()
    currentLoad = load
    isLoading.value = true

    try {
      const loaded = await fetchTrack(trackUrl, load.signal)
      if (currentLoad !== load) return
      const ordered = inReadingOrder(loaded.cues)
      cues.value = ordered
      documentUrl.value = loaded.documentUrl
      activeCueIndex.value = findActiveCueIndex(ordered, currentTime.value)
    } catch (e: any) {
      if (currentLoad === load && e.name !== 'AbortError') {
        error.value = 'error'
      }
    } finally {
      if (currentLoad === load) {
        isLoading.value = false
        currentLoad = null
      }
    }
  }

  // A panel closed mid-load does not leave the download running.
  onScopeDispose(() => currentLoad?.abort())

  function seekToCue(cueIndex: number): void {
    const cue = cues.value[cueIndex]
    if (cue) {
      seek(cue.start)
    }
  }

  // Update active cue on time change
  watch(currentTime, (time) => {
    if (cues.value.length === 0) return

    // Optimization: check if current cue is still active
    const current = activeCueIndex.value
    if (current !== -1) {
      const cue = cues.value[current]
      if (cue.start <= time && time < cue.end) return

      // Check next cue (common case: sequential playback)
      const next = cues.value[current + 1]
      if (next && next.start <= time && time < next.end) {
        activeCueIndex.value = current + 1
        return
      }
    }

    activeCueIndex.value = findActiveCueIndex(cues.value, time)
  })

  // Another set of tracks (e.g. navigation in a collection) starts over on its default one. An item
  // with none is a reset like any other selection: the previous item's load is aborted and its
  // document dropped, so nothing of the previous video is offered or lands afterwards.
  watch(availableSubtitleTracks, (tracks) => {
    selectTrack(defaultTrackIndex(tracks))
  })

  // Auto-select on initial mount if tracks are already available
  if (availableSubtitleTracks.value.length > 0) {
    selectTrack(defaultTrackIndex(availableSubtitleTracks.value))
  }

  return {
    cues,
    activeCueIndex,
    selectedTrackIndex,
    isLoading,
    error,
    documentUrl,
    selectTrack,
    seekToCue,
  }
}
